/**
 * Top-level error boundary for the Player. Without this, any uncaught render
 * error (CORS-blocked fetch, malformed playlist, missing plugin, etc.) blanks
 * the WebView and leaves the user stuck at a white screen. With it, we show
 * a recovery UI with the actual error message + buttons to retry / log out.
 */
import React from "react";
import { AlertTriangle, RefreshCcw, LogOut } from "lucide-react";

export default class PlayerErrorBoundary extends React.Component {
  state = { error: null, info: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack around for the "Show details" toggle.
    this.setState({ error, info });
    // Surface to the host console in case devtools are open.
    // eslint-disable-next-line no-console
    console.error("[player] crashed", error, info);
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  reload = () => {
    // Hard reload — clears the component tree and any half-set context state.
    window.location.reload();
  };

  factoryReset = () => {
    // Last-resort: clear everything that might be in a bad state.
    try {
      localStorage.clear();
    } catch {}
    try {
      indexedDB.deleteDatabase("za_player_pro");
    } catch {}
    window.location.href = "/";
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-[#07080b] text-slate-100 flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[#11141b] border border-amber-500/30 rounded-2xl p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-1">Something went wrong</h2>
          <p className="text-xs text-slate-400 mb-4">
            The player ran into an unexpected error and stopped. Share the
            error below with support and use Reload or Reset to recover.
          </p>
          <div className="text-left bg-black/40 border border-rose-500/30 rounded p-3 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-rose-300 font-semibold mb-1">
              Error
            </div>
            <pre className="whitespace-pre-wrap break-words text-rose-200 text-[12px]">
              {String(this.state.error?.message || this.state.error || "Unknown error")}
            </pre>
            {this.state.info?.componentStack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[10px] text-slate-500">
                  Stack trace
                </summary>
                <pre className="whitespace-pre-wrap break-words mt-1 text-slate-500 text-[10px]">
                  {this.state.info.componentStack.slice(0, 1500)}
                </pre>
              </details>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={this.reload}
              data-testid="player-error-reload"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-md px-4 py-2 flex items-center justify-center gap-2"
            >
              <RefreshCcw className="w-4 h-4" />
              Reload player
            </button>
            <button
              onClick={this.factoryReset}
              data-testid="player-error-reset"
              className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-sm rounded-md px-4 py-2 flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Factory reset
            </button>
          </div>
        </div>
      </div>
    );
  }
}
